import React, { useMemo } from 'react';
import { ModalEditPhotoProfileProps, ProfilePhotoEditFormFields } from './types';
import { Button, Hint } from '@vtbhome/juicyfront';
import { Body, Footer } from './modal-edit-photo-profile.styles';
import { Field, Form } from 'react-final-form';
import { useCurrentUserSelector } from '@entities/current-user/model/selectors';
import { useProfileEditMutation } from '@features/current-user/edit-profile/api/profile-edit-api';
import { CACHE_KEY_PROFILE_PHOTO_EDIT } from '@features/current-user/edit-profile/config/api-constants';
import { Typography } from '@shared/ui/typography/typography';
import { ProfilePhotoEditFormKeys } from '@features/current-user/edit-profile/config/form-constants';
import { InputFileValue } from '@shared/ui/input-file/types';
import { InputFile } from '@shared/ui/input-file/input-file';
import { parseFileUrl } from '@shared/lib/utils/parse-file-url/parse-file-url';
import { validateEditProfilePhoto } from '@features/current-user/edit-profile/lib/utils/form-edit-profile-photo-validate/form-edit-profile-photo-validate';
import { Modal } from '@shared/ui/modal/modal';
import { useResetModalForm } from '@shared/lib/hooks/use-reset-modal-form/use-reset-modal-form';

export const ModalEditPhotoProfile: React.FC<ModalEditPhotoProfileProps> = ({ isShow, onClose, onAccept }) => {
    const user = useCurrentUserSelector();
    const [editProfile, editProfileResult] = useProfileEditMutation({ fixedCacheKey: CACHE_KEY_PROFILE_PHOTO_EDIT });
    const isSendingForm = editProfileResult.isLoading;
    const formRef = useResetModalForm<ProfilePhotoEditFormFields>(isShow);

    const initialFiles = useMemo(() => {
        const photoUrl = user?.photoUrl || '';
        const parsedUrl = parseFileUrl(photoUrl);
        return {
            uploadedFiles: photoUrl
                ? [
                      {
                          url: photoUrl,
                          id: Math.random(),
                          name: parsedUrl.fileName,
                          extension: parsedUrl.fileExtension,
                      },
                  ]
                : [],
            localFiles: [],
        } as InputFileValue;
    }, [user]);

    const handleSubmit = async (fields: ProfilePhotoEditFormFields) => {
        if (!user) {
            return;
        }
        const file = fields[ProfilePhotoEditFormKeys.Photo].localFiles[0]?.file || undefined;
        const result = await editProfile({
            id: user.id,
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            middleName: user.middleName || '',
            phoneNumber: user.phoneNumber || '',
            departmentId: user.department?.id || 0,
            jobTitle: user.jobTitle || '',
            receivingPromotionalMaterials: user.receivingPromotionalMaterials || false,
            uploadedPhotoFile: file,
        });
        if (!result.error) {
            onAccept();
        }
    };

    return (
        <Form
            subscription={{}}
            onSubmit={handleSubmit}
            validate={validateEditProfilePhoto}
            render={(form) => {
                formRef.current = form;
                return (
                    <Modal
                        isShow={isShow}
                        size='l'
                        showLine={false}
                        header={<Typography size='h5'>Изменение фотографии</Typography>}
                        footer={
                            <Footer>
                                <Button disabled={isSendingForm} buttonType='light' onClick={onClose}>
                                    Отменить
                                </Button>
                                <Button
                                    buttonType='primary'
                                    preloader={isSendingForm}
                                    disabled={isSendingForm}
                                    onClick={form.handleSubmit}>
                                    Подтвердить
                                </Button>
                            </Footer>
                        }
                        onClose={onClose}>
                        <Hint title='Требования к фотографии' variant='blue'>
                            Фотография должна быть формата 1:1, размер не более 5мб
                        </Hint>
                        <Body>
                            <Field
                                name={ProfilePhotoEditFormKeys.Photo}
                                initialValue={initialFiles}
                                render={({ input, meta }) => {
                                    return (
                                        <InputFile
                                            name={ProfilePhotoEditFormKeys.Photo}
                                            value={input.value}
                                            error={meta.touched && meta.error}
                                            placeholder='Загрузить фотографию'
                                            accept='.jpeg,.jpg,.png'
                                            count={1}
                                            onChange={input.onChange}
                                        />
                                    );
                                }}
                            />
                        </Body>
                    </Modal>
                );
            }}
        />
    );
};
